import { create } from 'zustand';
import { PermissionMapper } from './util';

interface State {
  permissions: PermissionMapper;
}

interface Action {
  setPermissions: (permissions: PermissionMapper) => void;
}

const initialState: State = {
  permissions: {
    account_list: true,
    'home-page': true,
    'signed-operations': true,
    'payment-orders': true,
    templates: true,
    settings: true,
    'transfer-page': {
      anipay: true,
      payment_acc2acc: true,
      payment_interbank: true,
      payment_local_transfer: true,
      payment_foreign_transfer: true,
      payment_conversion: true,
    },
    'payment-page': {
      HOP: true,
      payment_budget: true,
      payment_edv: true,
      other_payments: true,
      preflight_fee: true,
    },
    salary_list: {
      'employee-table': true,
      'new-payments': true,
      'payments-history': true,
    },
    'salary-card': {
      salary_card: true,
      salary_cards_prolong: true,
      salary_cards_statuses: true,
    },
    guarantee: {
      'guarantee-v2': true,
      loan_list: true,
    },
    loan: {
      request_loan: true,
      use_loan_limit: true,
    },
  },
};

// A shallow `{ ...defaults, ...incoming }` replaces object-shaped pages
// (transfer-page, payment-page) wholesale, so any key the incoming menu
// response didn't populate would silently drop to undefined instead of
// falling back to its default. Merge one level deeper for those pages.
const mergePermissions = (
  defaults: PermissionMapper,
  incoming: Partial<PermissionMapper>
): PermissionMapper => {
  const merged = { ...defaults, ...incoming } as PermissionMapper;

  (Object.keys(defaults) as Array<keyof PermissionMapper>).forEach(key => {
    const defaultValue = defaults[key];

    // Same union-key limitation noted in util.ts: TS can't narrow `defaults[key]`
    // to an object type from the `typeof` check alone, so cast through `any`.
    if (typeof defaultValue === 'object') {
      merged[key] = { ...(defaultValue as any), ...(incoming[key] as any) };
    }
  });

  return merged;
};

const usePermissions = create<State & Action>(set => ({
  ...initialState,
  setPermissions: permissions =>
    set({ permissions: mergePermissions(initialState.permissions, permissions) }),
}));

export default usePermissions;
